// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { IKeytipProps, IStyle } from "@fluentui/react";

import type { AppButtonProps } from "@1js/acui-button";
import type { MenuOpenOnExecuteParameter } from "@1js/acui-common";
import type {
  ControlInMenuRendererFunction,
  MenuDefinition,
} from "@1js/acui-menu";

import type { ControlRendererFunction } from "../../../controls";
import type { RibbonControlProps } from "../../Controls/RibbonControlProps";
import type { RibbonScalingStep } from "../../Scaling";
import type {
  RibbonControlGroupDefinitionSetBase,
  RibbonControlGroupDefinitionSetMLR,
  RibbonControlGroupDefinitionSetSLR,
  RibbonMode,
  RibbonModeMultiline,
  RibbonModeSingleLine,
} from "../Ribbon/Ribbon.Props";
import type { Layout } from "../RibbonControlGroupMLR/layoutTypings";
import type { AppContextType } from "@1js/acui-context";

export const hiddenControlStyle = { display: "none" };
export const hiddenMeasurableControlStyle = {
  display: "inline-flex",
  position:
    "absolute" as "absolute" /* needed so typescript will not complain */,
  visibility: "hidden",
};
export const makeMeasurableControlVisibleStyle = {
  position:
    "relative" as "relative" /* needed so typescript will not complain */,
  visibility: "inherit",
};
export const makeControlVisibleStyle = { display: "inline-flex" };

// Task 6050121: Look at removing the !important from combobox margins
export const unpinComboboxStyle = { marginLeft: "0 !important" };
export type Selectors = { [selector: string]: IStyle };
export type ScalingStepsStyle = IStyle & {
  selectors: { [selectors: string]: IStyle };
};
export type WithSectionId<T> = T & { sectionId?: string };

export interface ScaledRibbonGroupBase {
  type: RibbonMode;
  id: string;
  groupLabel: string;
  fastDisabled?: boolean | (() => boolean | undefined);
}

export interface ScaledRibbonGroupMLR extends ScaledRibbonGroupBase {
  type: RibbonModeMultiline;
  id: string;
  groupLabel: string;
  controls: RibbonControlProps[];
  layout?: Layout;
  responsiveLayout?: Layout;
  groupLauncher?: AppButtonProps;
  hideGroupLabel?: boolean;
  // VSO:4229595 - temporarily disable animations for this control group.
  disableAnimations?: boolean;
  forceHideGroupLabelOnly?: boolean;
}

export interface ScaledRibbonGroupSLR extends ScaledRibbonGroupBase {
  type: RibbonModeSingleLine;
  overflowMenuLauncherDisabled?: boolean;
  overflowMenuLauncherLabel: string;
  onOverflowMenuOpen?: (parameter: MenuOpenOnExecuteParameter) => void;
  onOverflowMenuClose?: (id: string) => void;
  hasOwnOverflow?: boolean;
  shouldAllowAllControlsInOverflow?: boolean;
  topLevelControls: RibbonControlProps[];
  overflowControls: RibbonControlProps[];
  keytipProps?: IKeytipProps;
}

export type ScaledRibbonGroup = ScaledRibbonGroupMLR | ScaledRibbonGroupSLR;

/**
 * Used to maintain the current state of the Ribbon controls
 * after a given set of scaling transformations.
 */
export interface RibbonScalingStateBase {
  id: string;
  /**
   * The definition for each Ribbon control group
   */
  ribbonGroupDefinitions: RibbonControlGroupDefinitionSetBase;

  /**
   * The list of scaling steps to apply to this set of control groups.
   */
  scalingSteps: Readonly<RibbonScalingStep>[];

  /**
   * This is an index into the scaling steps array which denotes the number of scaling steps
   * that have been applied to yield the scaledRibbonGroups. A value of zero denotes that no
   * scaling steps have been applied yet.
   */
  appliedScalingStepCount: number;

  /**
   * The Ribbon supports an initial scaling state which is always applied, regardless of how much
   * real estate is available. This index represents the minimum number of scaling steps to apply
   * in order to provide that behavior.
   */
  minimumScalingStepsToApply: number;

  /**
   * The transformed Ribbon groups after the scaling steps were applied.
   */
  scaledRibbonGroups: ScaledRibbonGroupBase[];

  /**
   * The estimated width of the ribbon content at the given scaling step. When present, this will
   * be used by the resizeGroup to bypass unnecessary renders
   */
  estimatedWidth?: number;
}
export type RibbonScalingStateSLRWithCacheKeys = RibbonScalingStateSLR & {
  cacheKey: string;
  renderingCacheKey?: string;
};
export type RibbonScalingStateMLRWithCacheKeys = RibbonScalingStateMLR & {
  cacheKey: string;
  renderingCacheKey?: string;
};
export type RibbonScalingStateWithCacheKeys =
  | RibbonScalingStateSLRWithCacheKeys
  | RibbonScalingStateMLRWithCacheKeys;

/**
 * Used to maintain the current state of the Ribbon controls
 * after a given set of scaling transformations.
 */
export interface RibbonScalingStateSLR extends RibbonScalingStateBase {
  type: "SingleLine";
  /**
   * The definition for each Ribbon control group
   */
  ribbonGroupDefinitions: RibbonControlGroupDefinitionSetSLR;

  /**
   * The transformed Ribbon groups after the scaling steps were applied.
   */
  scaledRibbonGroups: ScaledRibbonGroupSLR[];

  /**
   * Combined overflow menu to show at the end of Ribbon. It collects all items from scaled ribbon
   * groups that requested its overflow menu to be moved to the end of ribbon
   */
  scaledEndOfRibbonOverflowMenu: MenuDefinition;
}

/**
 * Used to maintain the current state of the Ribbon controls
 * after a given set of scaling transformations.
 */
export interface RibbonScalingStateMLR extends RibbonScalingStateBase {
  type: "Multiline";

  /**
   * The definition for each Ribbon control group
   */
  ribbonGroupDefinitions: RibbonControlGroupDefinitionSetMLR;

  /**
   * The transformed Ribbon groups after the scaling steps were applied.
   */
  scaledRibbonGroups: ScaledRibbonGroupMLR[];

  /**
   * The transformed Ribbon groups after only the initial scaling steps were applied.
   */
  initialScaledRibbonGroups: ScaledRibbonGroupMLR[];

  /**
   * Render functions for each control. This is required to render a ribbon group within a menu
   */
  controlRendererFunction?: ControlRendererFunction;

  /**
   * Menu render functions for each control. This is required to render a ribbon group within a menu
   */
  controlInMenuRendererFunction?: ControlInMenuRendererFunction;
}

export type RibbonScalingState = RibbonScalingStateSLR | RibbonScalingStateMLR;

/**
 * Data cached on a user's machine to allow for faster ribbon loads in subsequent sessions.
 * This data will be cached for each window mode to allow persisted caching through orientation changes
 * windowMode will be of one of the WindowMode type values, but typescript requires the index to be a string or number
 * This typing is asserted when reading the value from localStorage
 */
export interface CachedRibbonScalingState {
  [windowMode: string]: RibbonScalingData;
}

export interface RibbonScalingData {
  windowWidth: number;
  slrScalingState?: ScalingStepsAppliedDict;
  mlrScalingState?: ScalingStepsAppliedDict;
  version: number;
}

export interface ScalingStepsAppliedDict {
  [tabId: string]: number;
}

/**
 * The EstimatedScalingWidthDict holds a map of the estimated width of the ribbon after applying each scaling step
 */
export interface EstimatedScalingWidthDict {
  [scalingStep: number]: number;
}

export type VirtualRibbonScalingState =
  | VirtualRibbonScalingStateSLR
  | VirtualRibbonScalingStateMLR;

/**
 * The VirtualRibbonScalingState holds up-to-date information of the layout-impacting properties for ribbon controls that have been
 * modified during scaling estimation. Since scaling steps can build off of each other, you can't always trust the information that's
 * in the DOM. For instance, If you drop the label on the Bold button, then come back later and move it to the overflow, you need to know
 * that this control "currently" exists without a label, even though it still has a label in the DOM.
 */
export interface VirtualRibbonScalingStateSLR {
  controls: ScaledControlsMap;
  groups: ScaledGroupsSLRMap;
  containsSingleOverflow: boolean;
}

export interface VirtualRibbonScalingStateMLR {
  controls: ScaledControlsMap;
  groups: ScaledGroupsMLRMap;
  knownWidths?: BuildTimeControlWidths;
}

interface ScaledControlsMap {
  [controlId: string]: ScaledControlData;
}

/**
 * Control calculations used during QuickScaling and derived from either the DOM or from data known at build-time
 */
interface ScaledControlData {
  fullControlWidth: number;
  labelWidth?: number;
  controlState?: ControlState;
  previewGalleryItemWidths?: number[];
  ghostFlyout?: number;
  ghostFlyoutTextContainer?: number;
  groupId?: string;
}

export enum ControlState {
  PartialGallery /*Represents a preview gallery that has dropped some of its top-level buttons*/,
  FullControl /*Represents a full control in its default state */,
  LabelDropped /*Represents a control that has dropped its label*/,
  MovedToOverflow /*Represents a control that has been moved into an overflow menu*/,
}

interface ScaledGroupsSLRMap {
  [groupId: string]: GroupStateSLR;
}

interface GroupStateSLR {
  containsOverflow: boolean;
  topLevelControlCount: number;
}

export interface ControlDimensions {
  componentRoot: number;
  textContainer?: number;
  previewGalleryItems?: number[];
  ghostFlyout?: number;
  ghostFlyoutTextContainer?: number;
}

interface ScaledGroupsMLRMap {
  [groupId: string]: GroupStateMLR;
}

interface GroupStateMLR {
  layoutIndex: number;
  isFlyout: boolean;
  width?: number;
}

/**
 * The dictionary of control dimensions that we collect at build-time and use for faster scaling
 */
export interface BuildTimeControlWidths {
  [controlId: string]: ControlDimensions;
}

export type FullBuildTimeControlWidths = Record<
  RibbonMode,
  BuildTimeControlWidths
>;

/**
 * The options/parameters for the ribbon's getScaleDown functions
 */
export interface GetScaleDownFunctionOptions {
  quickScalingIsEnabled?: boolean;
  measureContentVisibly: boolean;
  onError?: (message: string) => void;
  mode?: RibbonMode;
  appContext: AppContextType;
  safeguardState?: RibbonScalingSafeguardState;
}

/**
 * Used to record that a specific tab (e.g. Home or Insert) has initially rendered,
 * per ribbonMode
 */
export type InitialTabRenderRecords = Record<
  RibbonMode,
  { [name: string]: boolean }
>;

/**
 * Properties used to create the className for responsive scaling
 */
export type ScalingClassNameProperties<
  P extends
    | RibbonScalingStateSLRWithCacheKeys
    | RibbonScalingStateMLRWithCacheKeys,
> = {
  // The scaling state used to build the className
  scalingState: P;

  // The known control widths used to help calculate the className
  knownWidths: BuildTimeControlWidths;

  // How much space is reserved for non-Ribbon elements.
  reservedSpace: number;

  // Should we force a recalc? This can be true if we know we need to calculate a new width
  forceRecalc: boolean;

  // Optional, Is the react MLR enabled?
  reactMLRIsEnabled?: boolean;

  // Optional, Should we make sure we have all widths measured (including textContainer)?
  ensureFullWidths?: boolean;

  // Optional, Is this the initial render for tab associated with the scaling state
  initialRender?: boolean;

  /**
   * AppContext to be able to check flights
   */
  appContext: AppContextType;

  /**
   * modified estimated widths dict that should be used instead of computing a new one
   */
  modifiedEstimatedWidths?: EstimatedScalingWidthDict;

  /**
   * If true, update the global estimated widths value, not just the one stored locally for each mode
   */
  setGlobalEstimatedWidths?: boolean;
};

// The default scaling state that we'll boot into. All optimizations are active
export const CoreScalingState = "CoreScaling";
// We aren't able to scale with full optimization. Measure all ribbon contents and estimate scaling manually
export const MeasureFallbackState = "MeasureFallback";
// We still aren't able to scale correctly, manually update our scaling breakpoints based on the computed offset
export const ApplyAdditionalStepsState = "ApplyAdditionalSteps";
type RibbonScalingSafeguardType =
  | typeof CoreScalingState
  | typeof MeasureFallbackState
  | typeof ApplyAdditionalStepsState;

/**
 * Scaling fallback information to track the current scaling behavior across the ribbon
 * If our full scaling optimizations under-scale the ribbon, we'll alter the scaling state and try again with more precision
 */
export interface RibbonScalingSafeguardState {
  type: RibbonScalingSafeguardType;
  renderCount: number;
}
